import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  FileText,
  CheckCircle2,
  XCircle,
  Star,
  Loader2,
  Download,
  Mail,
  Trash2,
  AlertTriangle,
  UserCheck,
  UserX,
} from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * ============================================================
 * ADMIN WORKSHOP REGISTRATIONS VIEWER
 * ============================================================
 * Strictly manages 3 applicant statuses:
 *   selected | rejected | attended
 *
 * Rules:
 * - 'selected' consumes 1 seat
 * - 'attended' consumes 1 seat
 * - 'rejected' does NOT consume a seat
 * - Deleting an applicant releases their seat immediately
 * - Clean human-readable slug routes (/admin/workshops/:slug/registrations)
 * ============================================================
 */

const REGISTRATION_STATUSES = ["selected", "rejected", "attended"];

const STATUS_CONFIG = {
  selected: {
    label: "Selected",
    icon: CheckCircle2,
    classes: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    classes: "bg-rose-50 text-rose-700 border-rose-200",
  },
  attended: {
    label: "Attended",
    icon: Star,
    classes: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
};

function StatusBadge({ status }) {
  const normalized = (status || "selected").toLowerCase();
  const cfg = STATUS_CONFIG[normalized] || STATUS_CONFIG.selected;
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wider border",
        cfg.classes
      )}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function MetricCard({ label, value, subtext, colorClass }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-xs">
      <div className={cn("text-2xl font-bold tracking-tight", colorClass)}>{value}</div>
      <div className="text-[11px] text-slate-500 font-medium mt-0.5">{label}</div>
      {subtext && <div className="text-[10px] text-slate-400 font-medium mt-0.5">{subtext}</div>}
    </div>
  );
}

export default function AdminWorkshopRegistrations() {
  const { slug, id } = useParams();
  const identifier = slug || id;
  const navigate = useNavigate();

  const [workshop, setWorkshop] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  // Status Filter: 'all' | 'selected' | 'rejected' | 'attended'
  const [statusFilter, setStatusFilter] = useState("all");

  // Deletion modal state
  const [deletingApplicant, setDeletingApplicant] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reload workshop data to sync live seat count
  const refreshWorkshop = useCallback(async () => {
    if (!identifier) return;
    try {
      const wData = await workshopsService.getWorkshopBySlugOrId(identifier, true);
      if (wData) {
        setWorkshop(wData);
        // Automatically redirect legacy UUID to human-readable slug route if available
        if (wData.slug && identifier !== wData.slug) {
          navigate(`/admin/workshops/${wData.slug}/registrations`, { replace: true });
        }
      }
    } catch (err) {
      console.error("[AdminWorkshopRegistrations] Refresh workshop error:", err);
    }
  }, [identifier, navigate]);

  // Load workshop + registrations in parallel
  useEffect(() => {
    let mounted = true;
    if (!identifier) return;

    setLoading(true);
    Promise.all([
      workshopsService.getWorkshopBySlugOrId(identifier, true),
      workshopsService.getWorkshopRegistrations(identifier),
    ])
      .then(([wData, rData]) => {
        if (!mounted) return;
        setWorkshop(wData);
        setRegistrations(rData || []);

        if (wData?.slug && identifier !== wData.slug) {
          navigate(`/admin/workshops/${wData.slug}/registrations`, { replace: true });
        }
      })
      .catch((err) => {
        console.error("Error loading registrations:", err);
        if (mounted) toast.error("Failed to load registrations");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [identifier, navigate]);

  // Update a single registration status
  const handleStatusChange = useCallback(
    async (registrationId, newStatus) => {
      setUpdatingId(registrationId);
      try {
        await workshopsService.updateRegistrationStatus(registrationId, newStatus);
        setRegistrations((prev) =>
          prev.map((r) =>
            r.id === registrationId ? { ...r, status: newStatus } : r
          )
        );
        // Immediately refresh workshop seat count
        await refreshWorkshop();
        toast.success(`Status updated to ${newStatus}`);
      } catch (err) {
        console.error("Status update error:", err);
        toast.error(err.message || "Failed to update status");
      } finally {
        setUpdatingId(null);
      }
    },
    [refreshWorkshop]
  );

  // Destructive Delete Applicant Confirmation
  const handleConfirmDelete = async () => {
    if (!deletingApplicant) return;
    setIsDeleting(true);
    try {
      await workshopsService.deleteRegistration(deletingApplicant.id);
      setRegistrations((prev) => prev.filter((r) => r.id !== deletingApplicant.id));
      await refreshWorkshop();
      toast.success(`Applicant ${deletingApplicant.name} deleted and seat released`);
      setDeletingApplicant(null);
    } catch (err) {
      console.error("Delete applicant error:", err);
      toast.error(err.message || "Failed to delete applicant");
    } finally {
      setIsDeleting(false);
    }
  };

  // Metrics derived from live registrations state and workshop capacity
  const counts = {
    total: registrations.length,
    selected: registrations.filter((r) => (r.status || "").toLowerCase() === "selected").length,
    attended: registrations.filter((r) => (r.status || "").toLowerCase() === "attended").length,
    rejected: registrations.filter((r) => (r.status || "").toLowerCase() === "rejected").length,
  };

  const enrolledSeats = counts.selected + counts.attended;
  const capacity = workshop?.capacity || 0;
  const availableSeats = Math.max(0, capacity - enrolledSeats);

  // Filtered registrations
  const filteredRegistrations = registrations.filter((r) => {
    if (statusFilter === "all") return true;
    return (r.status || "").toLowerCase() === statusFilter;
  });

  return (
    <div className="space-y-8">
      {/* Back button */}
      <div>
        <Link
          to="/admin/workshops"
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-medium transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Workshops List</span>
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Workshop Applicants
            </h1>
            {workshop?.slug && (
              <span className="text-xs px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 font-mono border border-slate-200">
                {workshop.slug}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-600 mt-1 font-medium">
            {workshop ? workshop.title : `Workshop: ${identifier}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={workshop ? `/admin/workshops/${workshop.slug || workshop.id}/edit` : "#"}
            className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline font-medium"
          >
            Edit Workshop Details
          </Link>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs border-slate-200 bg-slate-50 text-slate-400 gap-1.5 cursor-not-allowed opacity-60 font-medium"
            disabled
            title="CSV Export coming in a future update"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>

      {/* Metrics strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard
          label="Total Applicants"
          value={counts.total}
          subtext="All submissions"
          colorClass="text-slate-900"
        />
        <MetricCard
          label="Selected"
          value={counts.selected}
          subtext="Consuming 1 seat"
          colorClass="text-emerald-600"
        />
        <MetricCard
          label="Attended"
          value={counts.attended}
          subtext="Consuming 1 seat"
          colorClass="text-cyan-600"
        />
        <MetricCard
          label="Rejected"
          value={counts.rejected}
          subtext="0 seats consumed"
          colorClass="text-rose-600"
        />
        <MetricCard
          label="Enrolled / Capacity"
          value={`${enrolledSeats} / ${capacity}`}
          subtext={`${Math.min(100, Math.round((enrolledSeats / (capacity || 1)) * 100))}% cohort fill`}
          colorClass="text-amber-600"
        />
        <MetricCard
          label="Available Seats"
          value={availableSeats}
          subtext={availableSeats === 0 ? "Cohort Full" : "Spots remaining"}
          colorClass={availableSeats === 0 ? "text-rose-600 font-extrabold" : "text-emerald-600"}
        />
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3 overflow-x-auto">
        <button
          onClick={() => setStatusFilter("all")}
          className={cn(
            "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5",
            statusFilter === "all"
              ? "bg-slate-100 text-slate-900 font-semibold"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
          )}
        >
          <span>All Applicants</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200/70 text-slate-700 font-semibold">
            {counts.total}
          </span>
        </button>

        <button
          onClick={() => setStatusFilter("selected")}
          className={cn(
            "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5",
            statusFilter === "selected"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
          )}
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          <span>Selected</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
            {counts.selected}
          </span>
        </button>

        <button
          onClick={() => setStatusFilter("attended")}
          className={cn(
            "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5",
            statusFilter === "attended"
              ? "bg-cyan-50 text-cyan-700 border border-cyan-200 font-semibold"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
          )}
        >
          <Star className="w-3.5 h-3.5 text-cyan-600" />
          <span>Attended</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cyan-100 text-cyan-800 font-semibold">
            {counts.attended}
          </span>
        </button>

        <button
          onClick={() => setStatusFilter("rejected")}
          className={cn(
            "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5",
            statusFilter === "rejected"
              ? "bg-rose-50 text-rose-700 border border-rose-200 font-semibold"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
          )}
        >
          <XCircle className="w-3.5 h-3.5 text-rose-600" />
          <span>Rejected</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-rose-100 text-rose-800 font-semibold">
            {counts.rejected}
          </span>
        </button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Applicants List ({filteredRegistrations.length})
          </div>
          <div className="text-[11px] text-slate-500 font-medium">
            Active Seats Consumed: <strong className="text-slate-900">{enrolledSeats}</strong> / {capacity}
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
            <span className="text-xs text-slate-500 font-medium">Loading student applications...</span>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center">
            <Users className="w-8 h-8 text-slate-300" />
            <p className="text-xs text-slate-500 font-medium">
              {statusFilter === "all"
                ? "No registrations received yet for this workshop."
                : `No applicants with status "${statusFilter}".`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                <tr>
                  <th className="px-5 py-3 font-semibold">Student Name</th>
                  <th className="px-5 py-3 font-semibold">College / University</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Resume</th>
                  <th className="px-5 py-3 font-semibold">Applied</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Update Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-700 font-medium">
                {filteredRegistrations.map((reg) => {
                  const isUpdating = updatingId === reg.id;
                  const resumeHref = reg.signed_resume_url || null;
                  const currentStatus = (reg.status || "selected").toLowerCase();

                  return (
                    <tr
                      key={reg.id}
                      className={cn(
                        "hover:bg-slate-50/60 transition-colors",
                        isUpdating && "opacity-60"
                      )}
                    >
                      {/* Name & Email */}
                      <td className="px-5 py-4">
                        <div className="font-bold text-slate-900">{reg.name}</div>
                        {reg.email && (
                          <a
                            href={`mailto:${reg.email}`}
                            className="inline-flex items-center gap-1 text-[11px] text-cyan-600 hover:text-cyan-700 hover:underline mt-0.5 font-medium"
                            title="Send email to applicant"
                          >
                            <Mail className="w-3 h-3 text-cyan-600 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{reg.email}</span>
                          </a>
                        )}
                      </td>

                      {/* College */}
                      <td className="px-5 py-4 text-slate-600">{reg.college}</td>

                      {/* Location */}
                      <td className="px-5 py-4 text-slate-500">
                        {reg.residing_city}, {reg.residing_state}
                      </td>

                      {/* Resume — signed URL only */}
                      <td className="px-5 py-4">
                        {resumeHref ? (
                          <a
                            href={resumeHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-cyan-600 hover:text-cyan-700 hover:underline font-medium"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[140px]">
                              {reg.resume_filename || "View Resume"}
                            </span>
                          </a>
                        ) : (
                          <span className="text-slate-400 text-[11px]">
                            {reg.resume_filename ? (
                              <span title="Signed URL unavailable">{reg.resume_filename}</span>
                            ) : (
                              "—"
                            )}
                          </span>
                        )}
                      </td>

                      {/* Applied date */}
                      <td className="px-5 py-4 text-slate-500">
                        {reg.created_at
                          ? new Date(reg.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>

                      {/* Status badge */}
                      <td className="px-5 py-4">
                        <StatusBadge status={currentStatus} />
                      </td>

                      {/* Unified Custom Select Dropdown */}
                      <td className="px-5 py-4">
                        <div className="w-36">
                          {isUpdating ? (
                            <div className="flex items-center gap-1.5 text-xs text-cyan-600">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Updating...</span>
                            </div>
                          ) : (
                            <Select
                              value={currentStatus}
                              onValueChange={(val) => handleStatusChange(reg.id, val)}
                            >
                              <SelectTrigger className="h-8 text-xs bg-slate-50 border-slate-200 text-slate-900 focus:ring-cyan-500 font-medium">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border border-slate-200 text-slate-900 shadow-lg">
                                <SelectItem value="selected" className="focus:bg-emerald-50 focus:text-emerald-700">
                                  <div className="flex items-center gap-1.5">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                    <span>Selected</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="attended" className="focus:bg-cyan-50 focus:text-cyan-700">
                                  <div className="flex items-center gap-1.5">
                                    <Star className="w-3.5 h-3.5 text-cyan-600" />
                                    <span>Attended</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="rejected" className="focus:bg-rose-50 focus:text-rose-700">
                                  <div className="flex items-center gap-1.5">
                                    <XCircle className="w-3.5 h-3.5 text-rose-600" />
                                    <span>Rejected</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </td>

                      {/* Delete Applicant Action */}
                      <td className="px-5 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingApplicant(reg)}
                          className="h-8 px-2.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors gap-1"
                          title={`Delete applicant ${reg.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline text-xs">Delete</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Destructive Confirmation Dialog for Deleting Applicant */}
      <AlertDialog
        open={Boolean(deletingApplicant)}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeletingApplicant(null);
        }}
      >
        <AlertDialogContent className="bg-white border-slate-200 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" />
              <span>Delete Applicant Registration?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-slate-600 space-y-3 mt-2">
              <p>
                Are you sure you want to permanently delete the registration record for{" "}
                <strong className="text-slate-900 font-semibold">
                  {deletingApplicant?.name}
                </strong>{" "}
                ({deletingApplicant?.email || "No email"})?
              </p>
              <div className="rounded-xl p-3 bg-rose-50 border border-rose-200 text-rose-700 text-[11px] leading-relaxed">
                ⚠️ <strong>Seat Availability Notice:</strong> If this applicant is currently{" "}
                <strong>Selected</strong> or <strong>Attended</strong>, their seat will be released immediately and become available to other candidates. This action cannot be undone.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 flex items-center gap-2">
            <AlertDialogCancel
              disabled={isDeleting}
              className="border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs rounded-xl"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs rounded-xl gap-1.5"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete Applicant</span>
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
